import React, { useState, useEffect } from "react";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  where
} from "firebase/firestore";
import { db } from "../firebase";
import useCurrentUser from "../hooks/useCurrentUser";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  Plus,
  Trash2,
  Edit2,
  Download,
  Upload,
  Grid,
  Settings
} from "lucide-react";

// Default columns if none defined
const DEFAULT_SCHEMA = {
  products: ["Material ID", "Description", "SKU", "Price", "Weight"],
  vendors: ["Vendor Name", "Contact Person", "Email", "Phone", "Address"],
  locations: ["Location ID", "Type", "Capacity", "Address"],
  customers: ["Customer Name", "Contact", "Email", "Region"],
};

// Map Tabs to their Firestore collection names to keep them lowercase
const TAB_TO_COLLECTION = {
  products: "products",
  vendors: "vendors",
  locations: "locations",
  customers: "customers"
};

export default function MasterData() {
  const { clientId } = useCurrentUser();
  const [activeTab, setActiveTab] = useState("products");
  const [data, setData] = useState([]);
  const [schema, setSchema] = useState([]);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSchemaModal, setShowSchemaModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form states
  const [newItem, setNewItem] = useState({});
  const [newColumn, setNewColumn] = useState("");

  useEffect(() => {
    if (!clientId) return;

    // 1. Subscribe to Schema
    const unsubSchema = onSnapshot(
      // We'll store schemas centrally in "settings" or a dedicated "schemas" root collection 
      // where document ID is clientId to segregate company schemas
      doc(db, "schemas", clientId),
      (snap) => {
        if (snap.exists()) {
          const allSchemas = snap.data();
          setSchema(allSchemas[activeTab] || DEFAULT_SCHEMA[activeTab] || []);
        } else {
          setSchema(DEFAULT_SCHEMA[activeTab] || []);
        }
      }
    );

    // 2. Subscribe to Data
    const collectionName = TAB_TO_COLLECTION[activeTab] || activeTab;
    const unsubData = onSnapshot(
      query(collection(db, collectionName), where("companyId", "==", clientId)),
      (snap) => {
        const rows = snap.docs.map((d) => ({ _id: d.id, ...d.data() }));
        setData(rows);
      }
    );

    return () => {
      unsubSchema();
      unsubData();
    };
  }, [clientId, activeTab]);

  // --- Handlers ---
  const handleSaveItem = async () => {
    const collectionName = TAB_TO_COLLECTION[activeTab] || activeTab;
    const id = editingItem ? editingItem._id : doc(collection(db, "dummy")).id; // generate ID
    const ref = doc(db, collectionName, id);
    await setDoc(ref, { ...newItem, companyId: clientId }, { merge: true });
    setShowAddModal(false);
    setEditingItem(null);
    setNewItem({});
  };

  const handleDelete = async (id) => {
    const collectionName = TAB_TO_COLLECTION[activeTab] || activeTab;
    if (!window.confirm("Delete this record?")) return;
    await deleteDoc(doc(db, collectionName, id));
  };

  const handleAddColumn = async () => {
    if (!newColumn.trim()) return;
    const updated = [...schema, newColumn.trim()];
    await setDoc(
      doc(db, "schemas", clientId),
      { [activeTab]: updated },
      { merge: true }
    );
    setNewColumn("");
  };

  const handleRemoveColumn = async (colName) => {
    if (!window.confirm(`Remove column "${colName}"?`)) return;
    const updated = schema.filter((c) => c !== colName);
    await setDoc(
      doc(db, "schemas", clientId),
      { [activeTab]: updated },
      { merge: true }
    );
  };

  // CSV Import/Export
  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, activeTab);
    XLSX.writeFile(wb, `${activeTab}_MasterData.xlsx`);
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        const rows = results.data;
        // Batch write ideally, but loop for simplicity
        for (const r of rows) {
          if (!Object.keys(r).length) continue;
          const collectionName = TAB_TO_COLLECTION[activeTab] || activeTab;
          const id = doc(collection(db, "dummy")).id;
          await setDoc(
            doc(db, collectionName, id),
            { ...r, companyId: clientId }
          );
        }
        alert(`Imported ${rows.length} records.`);
      },
    });
  };

  return (
    <div className="max-w-7xl mx-auto pb-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-navy-900 font-serif">Master Data</h1>
          <p className="text-slate-500 mt-1">Manage global definitions for products, vendors, and locations.</p>
        </div>
        <div className="flex gap-3">
          <label className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-lg cursor-pointer flex items-center gap-2 font-medium transition-colors">
            <Upload size={18} /> Import CSV
            <input type="file" accept=".csv" className="hidden" onChange={handleImport} />
          </label>
          <button
            onClick={handleExport}
            className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-lg flex items-center gap-2 font-medium transition-colors"
          >
            <Download size={18} /> Export
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-8 border-b border-slate-200 mb-6 overflow-x-auto">
        {Object.keys(DEFAULT_SCHEMA).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-4 text-sm font-bold uppercase tracking-wider transition-all whitespace-nowrap ${activeTab === tab
              ? "text-navy-900 border-b-2 border-navy-900"
              : "text-slate-400 hover:text-slate-600 border-b-2 border-transparent"
              }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-2 w-full md:w-auto">
          <h2 className="text-lg font-bold text-navy-900 flex items-center gap-2">
            <span className="bg-navy-50 text-navy-900 p-1.5 rounded-lg"><Grid size={18} /></span>
            {activeTab} Records
          </h2>
          <span className="text-slate-400 text-sm">({data.length})</span>
        </div>

        <div className="flex gap-2 w-full md:w-auto">
          <button
            onClick={() => setShowSchemaModal(true)}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium flex items-center gap-2 transition-colors"
          >
            <Settings size={18} /> Columns
          </button>
          <button
            onClick={() => {
              setEditingItem(null);
              setNewItem({});
              setShowAddModal(true);
            }}
            className="px-4 py-2 rounded-lg bg-navy-900 text-white hover:bg-navy-800 font-medium flex items-center gap-2 shadow-lg shadow-navy-900/20 transition-colors"
          >
            <Plus size={18} /> Add New
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden min-h-[400px]">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {schema.map((col) => (
                  <th key={col} className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider whitespace-nowrap">{col}</th>
                ))}
                <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((row) => (
                <tr key={row._id} className="hover:bg-slate-50 transition-colors">
                  {schema.map((col) => (
                    <td key={col} className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap">
                      {row[col] || "-"}
                    </td>
                  ))}
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditingItem(row);
                          setNewItem(row);
                          setShowAddModal(true);
                        }}
                        className="p-1.5 text-slate-400 hover:text-navy-900 hover:bg-slate-100 rounded transition"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(row._id)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={schema.length + 1} className="px-6 py-12 text-center text-slate-400">
                    No records found. Click "Add New" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h3 className="font-bold text-navy-900">{editingItem ? "Edit Record" : "New Record"}</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-red-500 font-bold">&times;</button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4">
              {schema.map((col) => (
                <div key={col}>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{col}</label>
                  <input
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 transition-all text-sm"
                    value={newItem[col] || ""}
                    onChange={(e) => setNewItem({ ...newItem, [col]: e.target.value })}
                  />
                </div>
              ))}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveItem}
                  className="px-4 py-2 bg-navy-900 text-white font-medium rounded-lg hover:bg-navy-800 shadow-lg shadow-navy-900/20 transition"
                >
                  Save Record
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schema Modal */}
      {showSchemaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h3 className="font-bold text-navy-900">Manage Columns</h3>
              <button onClick={() => setShowSchemaModal(false)} className="text-slate-400 hover:text-red-500 font-bold">&times;</button>
            </div>
            <div className="p-6">
              <div className="flex gap-2 mb-6">
                <input
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-900/20 focus:border-navy-900 text-sm"
                  placeholder="New column name..."
                  value={newColumn}
                  onChange={(e) => setNewColumn(e.target.value)}
                />
                <button
                  onClick={handleAddColumn}
                  className="bg-navy-900 text-white px-4 py-2 rounded-lg hover:bg-navy-800 transition"
                >
                  Add
                </button>
              </div>

              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {schema.map((col) => (
                  <div key={col} className="flex justify-between items-center p-3 bg-slate-50 border border-slate-200 rounded-lg group hover:border-slate-300 transition-colors">
                    <span className="text-sm font-medium text-slate-700">{col}</span>
                    <button
                      onClick={() => handleRemoveColumn(col)}
                      className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
